/**
 * Filter Management Helper Utilities
 *
 * This file contains utility functions for working with the unified filter management system.
 * It provides helpers for common filter operations, business logic, and filter transformations.
 */

import tooltipTexts from '../components/common/TooltipText.json'

/**
 * Get filter type from config
 */
export const getFilterType = (filterKey, config) => {
  if (!config?.data) return null

  for (const category of config.data) {
    const filter = category.filters.find((f) => f.key === filterKey)
    if (filter) return filter.type
  }

  return null
}

/**
 * Get filter config by key
 */
export const getFilterConfig = (filterKey, config) => {
  if (!config?.data) return null
  for (const category of config.data) {
    const filter = category.filters.find((f) => f.key === filterKey)
    if (filter) {
      return {
        ...filter,
        categoryId: category.id,
        categoryTitle: category.title
      }
    }
  }

  return null
}

/**
 * Get category by ID (internal helper)
 */
const getCategoryById = (categoryId, config) => {
  if (!config?.data) return null
  return config.data.find((cat) => cat.id === categoryId)
}

/**
 * Helper: Normalize state to array
 */
const normalizeStateToArray = (selectedState) => {
  if (Array.isArray(selectedState)) return selectedState
  if (selectedState) return [selectedState]
  return []
}

/**
 * Helper: Check if national view
 */
const isNationalViewCheck = (selectedStateArray, config) => {
  return (
    selectedStateArray.length > 0 &&
    (selectedStateArray.includes('All States') || selectedStateArray.includes(config?.entireUnitedStateLabel))
  )
}

/**
 * Check if a filter should be disabled based on business rules (internal helper)
 * @param {string} filterKey - The filter key to check
 * @param {Array|string} selectedState - Can be array of states or single state string
 * @param {Object} config - Filter configuration
 * @returns {boolean} - True if filter should be disabled
 */
const shouldFilterBeDisabled = (filterKey, selectedState, config) => {
  if (!config) return false

  const selectedStateArray = normalizeStateToArray(selectedState)
  const isNationalView = isNationalViewCheck(selectedStateArray, config)
  const hasSpecificStates = !isNationalView && selectedStateArray.length > 0

  // Rule 1: Childcare filter
  if (filterKey === 'estimatedDrivingDistanceChildcare') {
    if (isNationalView || !hasSpecificStates) return false
    if (config.childCareDisableState) {
      return selectedStateArray.every((state) => config.childCareDisableState.includes(state))
    }
  }

  // Rule 2: Geographic filters
  if (config.disableCommunitySupportAndLocationFiltersKey?.includes(filterKey)) {
    return isNationalView
  }

  // Rule 3: Filter dependencies
  if (config.filterDependencies?.[filterKey]) {
    const needsState = config.filterDependencies[filterKey].includes('state')
    return needsState && !hasSpecificStates
  }

  return false
}

/**
 * Apply childcare disable rule
 */
const applyChildcareDisableRule = (disabled, stateArray, config) => {
  if (!config.childCareDisableState) return
  const allStatesDisableChildcare = stateArray.every((state) => config.childCareDisableState.includes(state))
  if (allStatesDisableChildcare) {
    disabled.push('estimatedDrivingDistanceChildcare')
  }
}

/**
 * Apply filter dependencies rule
 */
const applyFilterDependenciesDisableRule = (disabled, hasSpecificStates, config) => {
  if (!config.filterDependencies) return

  for (const [filterKey, dependencies] of Object.entries(config.filterDependencies)) {
    const dependenciesMet = dependencies.every((dep) => (dep === 'state' ? hasSpecificStates : true))
    if (!dependenciesMet && !disabled.includes(filterKey)) {
      disabled.push(filterKey)
    }
  }
}

/**
 * Get all disabled filters based on current state(s) and business rules
 * @param {Array|string|null} selectedState - Can be array of states, single state, or null
 * @param {Object} config - Filter configuration
 * @returns {Array} - Array of disabled filter keys
 */
export const getDisabledFilters = (selectedState, config) => {
  const disabled = []
  if (!config) return disabled

  const selectedStateArray = normalizeStateToArray(selectedState)
  const isNationalView = isNationalViewCheck(selectedStateArray, config)
  const hasSpecificStates = !isNationalView && selectedStateArray.length > 0

  // Rule 1: Childcare - disabled if ALL selected states disable it
  if (hasSpecificStates) {
    applyChildcareDisableRule(disabled, selectedStateArray, config)
  }

  // Rule 2: Geographic filters - disabled for national view
  if (isNationalView && config.disableCommunitySupportAndLocationFiltersKey) {
    disabled.push(...config.disableCommunitySupportAndLocationFiltersKey)
  }

  // Rule 3: Filter dependencies
  applyFilterDependenciesDisableRule(disabled, hasSpecificStates, config)

  return disabled
}

/**
 * Count total active filters (internal helper)
 */
const countTotalActiveFilters = (activeFilters) => {
  return Object.keys(activeFilters).length
}

/**
 * Convert range filter value to API format
 */
const convertRangeFilter = (value) => {
  if (Array.isArray(value) && value.length === 2) {
    return {min: value[0], max: value[1]}
  }
  return value
}

/**
 * Convert checkbox filter value to API format
 */
const convertCheckboxFilter = (value) => {
  if (Array.isArray(value) && value.length > 0) {
    return value
  }
  return null // Signals to skip
}

/**
 * Convert toggle filter value to API format
 */
const convertToggleFilter = (value) => {
  // "all" (value=2 or null/undefined) means no filter applied
  if (value === 2 || value === 'all' || value === null || value === undefined) {
    return null // Signals to skip
  }
  return [Number(value)]
}

/**
 * Convert filter state to API query parameters
 * Format: {filterKey: {min: x, max: y}} for ranges, {filterKey: [values]} for checkboxes
 * Note: Outreach filters are handled separately via getBenefitsFromFilters()
 */
export const convertFiltersToQueryParams = (activeFilters, filterMetadata) => {
  const params = {}

  for (const [key, value] of Object.entries(activeFilters)) {
    const metadata = filterMetadata[key]
    if (!metadata) continue

    let convertedValue
    switch (metadata.type) {
      case 'range':
        convertedValue = convertRangeFilter(value)
        break
      case 'checkbox':
        convertedValue = convertCheckboxFilter(value)
        break
      case 'toggle':
        convertedValue = convertToggleFilter(value)
        break
      case 'outreach':
        // Handled separately via getBenefitsFromFilters()
        convertedValue = null
        break
      default:
        convertedValue = value
    }

    if (convertedValue !== null) {
      params[key] = convertedValue
    }
  }

  return params
}

/**
 * Get filter summary for display (e.g., "5 filters active")
 */
export const getFilterSummary = (activeFilters, config) => {
  const count = countTotalActiveFilters(activeFilters)

  if (count === 0) return 'No filters active'
  if (count === 1) return '1 filter active'
  return `${count} filters active`
}

/**
 * Get only the custom filter name from API (no fallback)
 * Returns null if no custom name exists
 * @param {string} filterKey - Filter key
 * @param {Object} customizations - Customizations from API
 * @returns {string|null} Custom name or null
 */
export const getFilterCustomName = (filterKey, customizations = {}) => {
  return customizations?.filterNames?.[filterKey] || null
}

/**
 * Get filter label from config (handles FILTER_CUSTOM_NAME)
 * Priority: customizations from API > base config name > filterKey
 */
export const getFilterLabel = (filterKey, config, customizations = {}) => {
  // Priority 1: Check if there's a custom name from API
  if (customizations?.filterNames?.[filterKey]) {
    return customizations.filterNames[filterKey]
  }

  // Priority 2: Get from base config
  const filterConfig = getFilterConfig(filterKey, config)
  return filterConfig?.name || filterKey
}

/**
 * Get only the custom category name from API (no fallback)
 * Returns null if no custom name exists
 * @param {string} categoryId - Category ID
 * @param {Object} customizations - Customizations from API
 * @returns {string|null} Custom name or null
 */
export const getCategoryCustomName = (categoryId, customizations = {}) => {
  return customizations?.categoryNames?.[categoryId] || null
}

/**
 * Get category label (handles FILTER_CATEGORY_CUSTOM_NAME)
 * Priority: customizations from API > base config title > categoryId
 */
export const getCategoryLabel = (categoryId, config, customizations = {}) => {
  // Priority 1: Check if there's a custom name from API
  if (customizations?.categoryNames?.[categoryId]) {
    return customizations.categoryNames[categoryId]
  }

  // Priority 2: Get from base config
  const category = getCategoryById(categoryId, config)
  return category?.title || categoryId
}

/**
 * Get tooltip text for disabled filter
 * @param {string} filterKey - The filter key
 * @param {Array|string} selectedState - Can be array of states or single state
 * @param {Object} config - Filter configuration
 * @returns {string|null} - Tooltip text or null if not disabled
 */
export const getDisabledFilterTooltip = (filterKey, selectedState, config) => {
  if (!shouldFilterBeDisabled(filterKey, selectedState, config)) {
    return null
  }

  // Normalize to array
  const selectedStateArray = normalizeStateToArray(selectedState)
  const isNationalView = isNationalViewCheck(selectedStateArray, config)
  const hasSpecificStates = !isNationalView && selectedStateArray.length > 0

  // Childcare specific
  if (filterKey === 'estimatedDrivingDistanceChildcare' && hasSpecificStates) {
    return tooltipTexts.sdohTooltipChildCareDisabled
  }

  // Geographic filters
  if (config?.disableCommunitySupportAndLocationFiltersKey?.includes(filterKey) && isNationalView) {
    return tooltipTexts.sdohTooltipNationView
  }

  // Dependency-based
  if (config?.filterDependencies?.[filterKey]) {
    const dependencies = config.filterDependencies[filterKey]
    const hasStateDep = dependencies.some((dep) => dep === 'state' && !hasSpecificStates)

    if (hasStateDep) {
      return tooltipTexts.sdohTooltipNationView
    }
  }

  return tooltipTexts.filterDisabled
}

/**
 * Convert outreach filter value to API format
 *
 * Input: { selected: ['SNAP', 'Medicare'], enrolledValue: 0 }
 * Output: [{ snapEnrolled: [0] }, { medicareEnrolled: [0] }]
 *
 * @param {Object} value - Outreach filter value
 * @param {Array} options - Filter options from config (contains enrolledKey mapping)
 * @returns {Array} - Array of enrolled key objects for API query
 */
export const convertOutreachToQueryFormat = (value, options = []) => {
  if (!value?.selected || value.selected.length === 0) {
    return []
  }

  // Create a map of value -> enrolledKey from options
  const enrolledKeyMap = {}
  for (const opt of options) {
    if (opt.value && opt.enrolledKey) {
      enrolledKeyMap[opt.value] = opt.enrolledKey
    }
  }

  // Convert enrolledValue: 0=No, 1=Yes, 2=All → [0], [1], [0,1]
  const getEnrolledArray = (enrolledValue) => {
    if (enrolledValue === 0) return [0]
    if (enrolledValue === 1) return [1]
    return [0, 1] // All
  }

  const enrolledArray = getEnrolledArray(value.enrolledValue)

  // Build array of enrolled key objects
  return value.selected
    .filter((selectedValue) => enrolledKeyMap[selectedValue])
    .map((selectedValue) => ({
      [enrolledKeyMap[selectedValue]]: enrolledArray
    }))
}

/**
 * Get outreach query params from applied filters
 * Returns the active outreach filter type and its data
 *
 * @param {Object} appliedFilters - All applied filters
 * @param {Object} filterMetadata - Metadata for each filter
 * @param {Object} config - Filter configuration
 * @returns {Object} - { type: 'benefits'|'workforce'|null, data: [] }
 */
export const getOutreachQueryParams = (appliedFilters, filterMetadata, config) => {
  // Map filter keys to their API types (keys must match config)
  const outreachTypeMap = {
    receivingBenefits: 'benefits',
    workforceRecruitment: 'workforce'
  }

  for (const [key, value] of Object.entries(appliedFilters)) {
    const metadata = filterMetadata[key]
    if (metadata?.type === 'outreach' && value?.selected?.length > 0) {
      // Find the filter config to get options with enrolledKey
      const filterConfig = getFilterConfig(key, config)
      if (filterConfig?.options) {
        const data = convertOutreachToQueryFormat(value, filterConfig.options)
        return {
          type: outreachTypeMap[key] || 'benefits',
          key,
          data
        }
      }
    }
  }

  return {type: null, key: null, data: []}
}
